import React from 'react';
import { useTenant } from '../contexts/TenantContext';
import { useCurrentUser } from '../contexts/AuthContext';
import { 
  useChannels, 
  useMessages, 
  useSendMessage, 
  useUpdateMessage,
  useDeleteMessage,
  useCreateChannel, 
  useWebSocket 
} from '../hooks/useChat';
import { ChannelItem } from '../components/chat/ChannelItem';
import { ChatWindow } from '../components/chat/ChatWindow';
import { Message } from '../types';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Spinner } from '../components/ui/Spinner';
import { Modal } from '../components/ui/Modal';
import { Plus } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { usePermissions } from '../hooks/usePermissions';
import { hasTierAccess } from '../constants';

import { useSearchParams } from 'react-router-dom';

export const ChatPage = () => {
  const { node } = useTenant();
  const { user } = useCurrentUser();
  const { isAdmin, isSystemAdmin, isSuperAdmin } = usePermissions();
  // Same policy the route guard uses (constants.ts), so the in-page lock can't drift.
  const isChatLocked = !hasTierAccess('chat', node?.tier, { isSystemAdmin, isSuperAdmin });
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const channelFromUrl = searchParams.get('channel');
  const [selectedChannelId, setSelectedChannelId] = React.useState<string | null>(channelFromUrl);
  const [messageText, setMessageText] = React.useState('');
  const [replyTo, setReplyTo] = React.useState<Message | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = React.useState(false);
  const [newChannelName, setNewChannelName] = React.useState('');
  
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const lastScrollTop = React.useRef<number>(0);
  const typingTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const { data: channels = [], isLoading: isLoadingChannels } = useChannels(node?.id || '');
  const { data: messages = [], isLoading: isLoadingMessages } = useMessages(selectedChannelId || '');
  const sendMessageMutation = useSendMessage(selectedChannelId || '');
  const updateMessageMutation = useUpdateMessage(selectedChannelId || '');
  const deleteMessageMutation = useDeleteMessage(selectedChannelId || '');
  const createChannelMutation = useCreateChannel(node?.id || '');

  const selectedChannel = React.useMemo(() => 
    channels.find(c => c.id === selectedChannelId), 
    [channels, selectedChannelId]
  );

  // WebSocket integration
  const onMessageReceived = React.useCallback((msg: Message) => {
    queryClient.setQueryData(['messages', selectedChannelId], (old: Message[] | undefined) => {
      if (!old) return [msg];
      if (old.some(m => m.id === msg.id)) return old;
      return [msg, ...old];
    });
  }, [selectedChannelId, queryClient]);

  const onMessageUpdated = React.useCallback((msg: Message) => {
    queryClient.setQueryData(['messages', selectedChannelId], (old: Message[] | undefined) => {
      if (!old) return [];
      return old.map(m => m.id === msg.id ? msg : m);
    });
  }, [selectedChannelId, queryClient]);

  const onMessageDeleted = React.useCallback((messageId: string) => {
    queryClient.setQueryData(['messages', selectedChannelId], (old: Message[] | undefined) => {
      if (!old) return [];
      return old.filter(m => m.id !== messageId);
    });
  }, [selectedChannelId, queryClient]);

  const { isConnected, presenceCount, setTyping, typingUsers } = useWebSocket(
    selectedChannelId || '', 
    onMessageReceived,
    onMessageUpdated,
    onMessageDeleted,
    isChatLocked
  );

  const handleTyping = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMessageText(e.target.value);
    
    // Set typing to true
    setTyping(true);
    
    // Clear existing timeout
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    
    // Set timeout to false after 2 seconds of no typing
    typingTimeoutRef.current = setTimeout(() => {
      setTyping(false);
    }, 2000);
  };

  const handleChannelSelect = (id: string) => {
    setSelectedChannelId(id);
    setSearchParams({ channel: id });
  };

  // Auto-select first channel if none selected. Selection is a render-phase
  // adjustment; the URL param is synced from an effect (external-system sync).
  const firstChannelId = channels[0]?.id ?? null;
  const [prevAutoChannelId, setPrevAutoChannelId] = React.useState<string | null>(null);
  if (channels.length > 0 && !selectedChannelId && !channelFromUrl && firstChannelId !== prevAutoChannelId) {
    setPrevAutoChannelId(firstChannelId);
    setSelectedChannelId(firstChannelId);
  }

  React.useEffect(() => {
    if (prevAutoChannelId && !channelFromUrl) {
      setSearchParams({ channel: prevAutoChannelId });
    }
  }, [prevAutoChannelId, channelFromUrl, setSearchParams]);

  // Scroll to bottom logic
  const scrollToBottom = React.useCallback((behavior: ScrollBehavior = 'smooth') => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior
      });
    }
  }, []);

  React.useEffect(() => {
    // Initial scroll or when messages change
    if (messages.length > 0) {
      const container = scrollRef.current;
      if (container) {
        const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 100;
        if (isAtBottom || lastScrollTop.current === 0) {
          scrollToBottom(lastScrollTop.current === 0 ? 'auto' : 'smooth');
        }
      }
    }
    lastScrollTop.current = scrollRef.current?.scrollTop || 0;
  }, [messages, scrollToBottom]);

  const handleSendMessage = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!messageText.trim() || !selectedChannelId) return;
    
    // Single source of truth: send via REST mutation (which triggers DB save + Redis pub/sub)
    sendMessageMutation.mutate(messageText);
    
    setMessageText('');
    setReplyTo(null);
  };

  const handleCreateChannel = () => {
    if (!newChannelName.trim() || !node) return;
    createChannelMutation.mutate({
      name: newChannelName,
      channelType: 'GROUP',
      isPrivate: false
    }, {
      onSuccess: () => {
        setIsCreateModalOpen(false);
        setNewChannelName('');
      }
    });
  };

  if (isLoadingChannels) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="flex h-full bg-white overflow-hidden -mx-6 -my-6 dark:bg-slate-900">
      {/* LEFT PANEL */}
      <div className="w-52 bg-white border-r border-black dark:border-white flex flex-col dark:bg-slate-900">
        <div className="p-4 bg-white/50 border-b border-black dark:border-white dark:bg-slate-950">
           <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-none bg-green-500" />
              <span className="text-[9px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 font-mono">
                {node ? 'Org Presence' : 'Global Presence'}
              </span>
           </div>
           <p className="text-[8px] text-gray-400 mt-1 leading-tight">
             {node ? `Visible only to ${node.name}` : 'Visible to all your orgs'}
           </p>
        </div>
        <div className="p-4 flex items-center justify-between">
          <span className="text-xs font-bold uppercase tracking-widest text-gray-400 font-mono">Channels</span>
          {isAdmin && (
            <button 
              onClick={() => setIsCreateModalOpen(true)}
              className="p-1 hover:bg-white rounded-none text-gray-500 dark:text-gray-400 transition-colors dark:bg-slate-800"
            >
              <Plus size={16} />
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-2 space-y-0.5">
          {channels.filter(c => c.channelType !== 'DIRECT').map(channel => (
            <ChannelItem
              key={channel.id}
              channel={channel}
              isSelected={selectedChannelId === channel.id}
              onClick={() => handleChannelSelect(channel.id)}
            />
          ))}

          <div className="pt-4 pb-2 px-2">
            <span className="text-xs font-bold uppercase tracking-widest text-gray-400 font-mono">Direct Messages</span>
          </div>

          {channels.filter(c => c.channelType === 'DIRECT').map(channel => (
            <ChannelItem
              key={channel.id}
              channel={channel}
              isSelected={selectedChannelId === channel.id}
              onClick={() => handleChannelSelect(channel.id)}
            />
          ))}
        </div>
      </div>

      {/* RIGHT PANEL */}
      <ChatWindow
        selectedChannel={selectedChannel || null}
        messages={messages}
        isLoadingMessages={isLoadingMessages}
        messageText={messageText}
        onMessageChange={handleTyping}
        onSendMessage={handleSendMessage}
        onUpdateMessage={(id, body) => updateMessageMutation.mutate({ messageId: id, body })}
        onDeleteMessage={(id) => deleteMessageMutation.mutate(id)}
        replyTo={replyTo}
        setReplyTo={setReplyTo}
        presenceCount={presenceCount}
        typingUsers={typingUsers}
        isConnected={isConnected}
        currentUserId={user?.id}
        scrollRef={scrollRef}
        isLocked={isChatLocked}
      />

      {/* Create Channel Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Create Channel"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setIsCreateModalOpen(false)}>Cancel</Button>
            <Button 
              variant="primary" 
              onClick={handleCreateChannel}
              disabled={!newChannelName.trim() || createChannelMutation.isPending}
            >
              Create Channel
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Input
            label="Channel Name"
            placeholder="e.g. project-x"
            value={newChannelName}
            onChange={(e) => setNewChannelName(e.target.value)}
            autoFocus
          />
          <p className="text-xs text-gray-500">
            Channels are where your team communicates. They’re best when organized around a topic — #marketing, for example.
          </p>
        </div>
      </Modal>
    </div>
  );
};
